/**
 * CustomerArrivedSheet — "Your driver has arrived" slide-up for customers, with
 * Call and "I'm coming" so nobody misses the pickup. Laundry / trash arrivals are
 * handled by their own payment sheet, so they are skipped here.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { MapPin, Phone, Check, Loader2, Timer, Footprints } from "lucide-react";
import { API, useAuth } from "@/App";
import PersistentSheet from "@/components/requests/PersistentSheet";
import { SERVICE_TONES, SERVICE_LABELS, serviceToneKey } from "@/lib/serviceTones";
import { useCustomerTripEvents, seenSet, markSeen } from "@/lib/useCustomerTripEvents";
import { proNoun } from "@/components/CustomerAcceptedSheet";
import { startRinging } from "@/lib/chime";
import { notifyBookingUpdated } from "@/lib/requestWindow";

const SEEN_KEY = "yorm:cust-arrived-seen";
const TYPES = ["rideshare_driver_arrived"];
const NO_SHOW_SECONDS = 15 * 60;

const canSayComing = (b) => (b?.rideshare_type || "").toLowerCase() !== "runner";

export default function CustomerArrivedSheet({ demo }) {
  const { token } = useAuth() || {};
  const navigate = useNavigate();
  const [trip, setTrip] = useState(demo || null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  useCustomerTripEvents(TYPES, (b, { onPage }) => {
    if (b.status !== "accepted" || !b.driver_arrived_at || b.rider_coming || onPage) return;
    if (["laundry", "trash"].includes(b.service_flavor) || seenSet(SEEN_KEY).has(b.id)) return;
    markSeen(SEEN_KEY, b.id);
    setTrip(b);
  });

  // Ring briefly, tick the no-show countdown, and close on our own if the trip moves on.
  useEffect(() => {
    if (!trip?.id || demo) return undefined;
    const stopRing = startRinging({ untilMs: Date.now() + 12000, everyMs: 3000 });
    const tick = setInterval(() => setNow(Date.now()), 1000);
    const watch = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/rideshare/bookings/${trip.id}?token=${token}`);
        if (data?.status !== "accepted" || data?.rider_coming) setTrip(null);
      } catch { setTrip(null); }
    }, 5000);
    return () => { if (typeof stopRing === "function") stopRing(); clearInterval(tick); clearInterval(watch); };
  }, [trip?.id, token, demo]);

  if (!trip) return null;
  const tone = SERVICE_TONES[serviceToneKey(trip)];
  const label = SERVICE_LABELS[serviceToneKey(trip)] || "Trip";
  const noun = proNoun(trip);
  const name = trip.artisan_name || `Your ${noun}`;
  const v = trip.driver_vehicle || {};
  const vehicle = [v.color, v.make, v.model].filter(Boolean).join(" ");
  const arrivedMs = Date.parse(trip.driver_arrived_at) || now;
  const remaining = Math.min(NO_SHOW_SECONDS, Math.max(0, NO_SHOW_SECONDS - Math.floor((now - arrivedMs) / 1000)));
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");

  const coming = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/rideshare/bookings/${trip.id}/rider-coming?token=${token}`);
      toast.success(`${name.split(" ")[0]} has been asked to wait.`);
      notifyBookingUpdated("rideshare_rider_coming", trip.id);
      setTrip(null);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't send confirmation";
      toast.error(msg);
      if (e?.response?.status === 400) setTrip(null);
    } finally { setBusy(false); }
  };
  const open = () => { const id = trip.id; setTrip(null); navigate(`/customer/trip/${id}`); };

  return (
    <PersistentSheet open title={`${name} has arrived`} icon={MapPin} testId="customer-arrived-sheet" className="pay-glass">
      <div className="space-y-4" style={{ "--svc": tone.main, "--svc-dark": tone.dark }}>
        <div className="flex flex-col items-center text-center pt-1">
          <span className="relative h-14 w-14 rounded-full flex items-center justify-center text-white shadow-md" style={{ background: tone.main }}>
            <span className="absolute inset-0 rounded-full animate-ping opacity-30" style={{ background: tone.main }} />
            <MapPin size={26} strokeWidth={2.5} className="relative" />
          </span>
          <p className="mt-3 text-[17px] font-semibold text-[#222]" data-testid="customer-arrived-headline">
            Your {noun} is at the pickup point
          </p>
          <p className="text-[13px] text-gray-500" data-testid="customer-arrived-sub">
            {label} · {vehicle || name}{v.plate_number ? ` · ${v.plate_number}` : ""} — head down now.
          </p>
        </div>

        <div className="rounded-2xl bg-white/80 p-4 space-y-2" data-testid="customer-arrived-summary">
          <p className="text-[11px] uppercase tracking-wide text-gray-500">Pickup</p>
          <p className="text-[14px] text-[#222] leading-snug" data-testid="customer-arrived-pickup">{trip.pickup_address || "Your pinned location"}</p>
          <div className="flex items-center gap-1.5 pt-2 border-t border-gray-100 text-[13px] text-amber-700" data-testid="customer-arrived-countdown">
            <Timer size={14} /> Waiting time left <span className="font-semibold tabular-nums">{mm}:{ss}</span>
          </div>
        </div>

        {canSayComing(trip) ? (
          <Button onClick={coming} disabled={busy} className="w-full h-12 rounded-xl text-white text-[16px] font-semibold bg-[var(--svc)] hover:bg-[var(--svc-dark)]" data-testid="customer-arrived-coming-btn">
            {busy ? <Loader2 size={18} className="animate-spin mr-2" /> : <Footprints size={18} className="mr-2" />} I'm coming
          </Button>
        ) : (
          <Button onClick={() => setTrip(null)} className="w-full h-12 rounded-xl text-white text-[16px] font-semibold bg-[var(--svc)] hover:bg-[var(--svc-dark)]" data-testid="customer-arrived-got-it-btn">
            <Check size={18} className="mr-2" /> Got it
          </Button>
        )}
        <div className="grid grid-cols-2 gap-2">
          {(trip.driver_phone || trip.artisan_phone) ? (
            <a href={`tel:${(trip.driver_phone || trip.artisan_phone)}`} className="h-11 rounded-xl bg-white/90 border border-gray-200 text-[15px] font-medium text-[#222] inline-flex items-center justify-center gap-2" data-testid="customer-arrived-call-btn">
              <Phone size={16} className="text-[var(--svc-dark)]" /> Call {name.split(" ")[0]}
            </a>
          ) : (
            <Button variant="outline" onClick={open} className="h-11 rounded-xl bg-white/90" data-testid="customer-arrived-call-btn">
              <Phone size={16} className="mr-2 text-[var(--svc-dark)]" /> Call from trip
            </Button>
          )}
          <Button variant="outline" onClick={open} className="h-11 rounded-xl bg-white/90" data-testid="customer-arrived-open-trip-btn">
            View trip
          </Button>
        </div>
      </div>
    </PersistentSheet>
  );
}
